import SpinnerIcon from '@img/icons/spinner.svg?raw';
import { deleteCookie, setCookie } from '@js/src/cookies';

export function debounce(callback, wait) {
    let timeoutId = null;

    return (...args) => {
        window.clearTimeout(timeoutId);

        timeoutId = window.setTimeout(() => {
            callback(...args);
        }, wait);
    };
}

export function buttonStartLoadingState(elem) {
    const loader = document.createElement('span');

    loader.className = 'loader size-4';
    loader.innerHTML = SpinnerIcon;

    elem.appendChild(loader);
}

export function buttonStopLoadingState(elem) {
    const loader = elem.querySelector('.loader');

    if (loader) {
        loader.remove();
    }
}

export function addClassForAndroid() {
    var ua = navigator.userAgent.toLowerCase();
    var isAndroid = ua.indexOf("android") > -1;

    if (isAndroid) {
        document.body.classList.add('is-android');
    }
}

export function hideElementWhenScrolledPastElement(triggerElement, targetElement) {
    if (!triggerElement || !targetElement) {
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                triggerElement.classList.add('is-hidden');
            } else {
                triggerElement.classList.remove('is-hidden');
            }
        });
    });

    observer.observe(targetElement);
}

export function cacheDates(startDate, endDate) {
    if (startDate) {
        setCookie('enquiry_start_date', startDate);
    } else {
        deleteCookie('enquiry_start_date');
    }

    if (endDate) {
        setCookie('enquiry_end_date', endDate);
    } else {
        deleteCookie('enquiry_end_date');
    }
}

export function helpers() {
    // Floating CTA: hide when the unified enquiry section enters the viewport
    // (and reappear when the user scrolls away). The class added is `is-hidden`
    // = opacity-0 + pointer-events-none; the CTA wrapper already carries
    // `lh-transition` (200ms ease) so the toggle fades in/out smoothly.
    // Target is #enquiry-block (visible on both breakpoints) — previously this
    // watched #enquire which is `hidden lg:block`, so the observer never fired
    // on mobile (display:none elements don't register intersection entries).
    document.querySelectorAll('[data-floating-cta]').forEach((el) => {
        hideElementWhenScrolledPastElement(el, document.querySelector('#enquiry-block'));
    });

    document.querySelectorAll('[data-pagination]').forEach((el) => {
        el.addEventListener('change', function () {
            const page = this.value;

            if (!page) {
                return;
            }

            const url = new URL(window.location.href);

            url.searchParams.set('paged', page);
            window.history.pushState({}, '', url);

            window.location.href = url.href;
        });
    });

    document.querySelectorAll('[data-select="url"]').forEach((el) => {
        el.addEventListener('change', function () {
            const url = this.value;

            if (!url) {
                return;
            }

            window.location.href = url;
        });
    });
}

/**
 * Run `callback` on DOMContentLoaded - or straight away if that event has
 * genuinely already fired.
 *
 * The entry scripts are ES modules. Normally they run before
 * DOMContentLoaded, and the listener catches it as it always has. But
 * WebKit has an intermittent race (bug 297939) where a module can run
 * after the event, and then a plain listener never fires: on 21 Oct 2026
 * the homepage search bar stayed as grey placeholder boxes in an iPad
 * private window until it had been reloaded a few times.
 *
 * Do NOT simplify this to `readyState !== 'loading'`. Deferred and module
 * scripts run while readyState is already 'interactive', so that test runs
 * the callback early on every normal load. app.js would then attach
 * sliders and links to #property before property.js mounts Vue over it,
 * and Vue's re-render drops them - dead galleries on every listing (caught
 * in review of #477). 'interactive' alone does not say whether the event
 * has fired; navigation timing does.
 *
 * Lives in helpers.js rather than a file of its own: helpers is already a
 * shared chunk that app.js loads on every page, so this adds no request.
 * As its own module it became a separate 337-byte chunk fetched on every
 * page (caught in review of #477).
 */
function domContentLoadedHasFired() {
    if (document.readyState === 'complete') {
        return true;
    }

    if (document.readyState === 'loading') {
        return false;
    }

    const nav = performance.getEntriesByType?.('navigation')?.[0];

    if (nav) {
        return nav.domContentLoadedEventStart > 0;
    }

    // Safari before 15 has no Navigation Timing Level 2. Level 1 is
    // deprecated but present everywhere: epoch ms, 0 until the event starts.
    return (performance.timing?.domContentLoadedEventStart || 0) > 0;
}

export function onDomReady(callback) {
    if (domContentLoadedHasFired()) {
        callback();
    } else {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
    }
}
